import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUsers,
	faTimes,
	faChevronDown
} from "@fortawesome/free-solid-svg-icons";
import { AppContext } from "../../../context";
import { validatePassengers } from "../utils";
import { PopoverInput } from "../../../components/PopoverInput";
import { StepperInput } from "../../../components/StepperInput";
import { SelectInput } from "../../../components/SelectInput";
import { Button, TextButton } from "../../../components/Button";
import { BRANDS, MODES, VALIDATIONS } from "../../../configs";
import {
	FLIGHTS_CLASS_ID,
	FLIGHTS_PASSENGERS_ID
} from "../../../configs/const";
import { isIOS, isBrowser } from "../../Shared/utils/detect-devices";
import { SovPassengerClassFieldInput } from "../ClassField/SovPassengerClassFieldInput";

import styles from "./PassengersField.scss";

const PassengersField = ({
	mode,
	className,
	maxChildAge,
	selectedClass,
	selectedPassengers,
	searchType,

	openRoomAndGuest,
	isGuestMobile,
	isInitialSovMobileInputState,

	classList,

	ParentRefElement,

	onChange,
	setSelectedClass,
	setShowClassInput,
	setIsInitialSovMobileInputState,
	handleSovMobileInputSelectionToView
}) => {
	const [useAppState] = AppContext.useAppContext();
	const isSOVMobile = useAppState.sovSimplifiedSearchBar;
	// IE 11 Fix - State is stale when passed used by validateSelection
	const latestPassengers = useRef(selectedPassengers);
	const [isValidAgeSelection, setIsValidAgeSelection] = useState([]);
	const [displayAgeError, setDisplayAgeError] = useState(false);
	const isStackMobileLayout = useAppState.brand === BRANDS.SOV && !useAppState.sovSimplifiedSearchBar;

	const stepperLabels = {
		decreaseValue: useAppState.getDictionary("decrease-value"),
		increaseValue: useAppState.getDictionary("increase-value")
	};

	const convertToDisplayValue = occupanciesData => {
		let displayValue = "";

		// Adults
		displayValue += ` ${occupanciesData?.adults} ${
			occupanciesData?.adults < 2
				? useAppState.getDictionary("adult")
				: useAppState.getDictionary("adults")
		}`;

		// Children
		if (occupanciesData?.children?.length) {
			displayValue += `, ${occupanciesData?.children?.length} ${
				occupanciesData?.children?.length < 2
					? useAppState.getDictionary("child")
					: useAppState.getDictionary("children")
			}`;
		}

		return displayValue;
	};

	const childAgeOptions = Array.from(Array(maxChildAge + 1).keys()).map(
		number => {
			// maxChildAge + 1 to account for first empty "--" option
			if (useAppState.brand === BRANDS.SOV) {
				if (number === 0) {
					if (searchType === "Flights") {
						return {
							label: 1,
							value: 1
						};
					}
					return {
						label: "--",
						value: "--"
					};
				}

				return {
					label: number + 1,
					value: number + 1
				};
			}

			if (number === 0) {
				return {
					label: "--",
					value: "--"
				};
			}
			return {
				label: number,
				value: number
			};
		}
	);

	if (searchType === "Flights") {
		childAgeOptions.unshift({ label: "< 1", value: 0 });
		childAgeOptions.unshift({ label: "--", value: "--" });
	}

	const resetValidation = () => {
		setIsValidAgeSelection([]);
		setDisplayAgeError(false);
	};

	const handleAdultsChange = value => {
		resetValidation();
		onChange({
			...selectedPassengers,
			adults: value
		});
	};

	const handleChildrenChange = value => {
		let newChildren = [...selectedPassengers.children];

		if (newChildren.length > value) {
			newChildren = newChildren.splice(0, value);
		} else {
			newChildren = newChildren.concat(
				new Array(value - newChildren.length).fill("--")
			);
		}

		resetValidation();
		onChange({
			...selectedPassengers,
			children: newChildren
		});
	};

	const validateSelection = () => {
		const validation = validatePassengers(latestPassengers.current);

		setIsValidAgeSelection(validation.isValidChildAges);
		setDisplayAgeError(
			!validation.isValidChildAges.every(validAge => validAge)
		);

		return validation.isValid;
	};

	useEffect(() => {
		latestPassengers.current = selectedPassengers;
	}, [selectedPassengers]);

	let _label = useAppState.getDictionary("passengers");
	if (useAppState.brand === BRANDS.SOV) {
		if (isSOVMobile) {
			_label = useAppState.getDictionary("who");
		} else {
			_label = useAppState.getDictionary("travellers");
		}
	}

	return (
		<PopoverInput
			mode={mode}
			label={_label}
			id={FLIGHTS_PASSENGERS_ID}
			brand={useAppState.brand}
			selectedClass={selectedClass}
			value={convertToDisplayValue(selectedPassengers)}
			defaultCloseIcon={false}
			setShowClassInput={setShowClassInput}
			className={cx(className, {
				[styles[useAppState.brand]]: styles[useAppState.brand]
			})}
			icon={
				<span className={styles.listViewButton} focus-trap-toggle="true">
					<FontAwesomeIcon
						icon={useAppState.brand === BRANDS.SWG ? faUsers : faChevronDown}
						className={styles.listViewIcon}
					/>
				</span>
			}
			ParentRefElement={ParentRefElement}
			openRoomAndGuest={openRoomAndGuest}
			isInitialSovMobileInputState={isInitialSovMobileInputState}
			onChange={onChange}
			setIsInitialSovMobileInputState={setIsInitialSovMobileInputState}
			reactDOM={useAppState.reactDOM}
			appRoot={useAppState.appRoot}
			isGuestMobile={isGuestMobile}
			onClose={() => {
				const isValid = validateSelection(selectedPassengers);
				if (isValid){
					window.WEB_CDN_BSB._emitter.emit("widgetToggle", false);
				}
				return isValid;
			}}
			handleSovMobileInputSelectionToView={handleSovMobileInputSelectionToView}>
			{({ handleClose }) => (
				<fieldset
					className={cx(styles.container, {
						[styles.containerIOSMobile]:
							selectedPassengers.children?.length > 0 && isIOS() && isSOVMobile,
						[styles.containerChromeMobile]:
							selectedPassengers.children?.length > 0 &&
							isBrowser() &&
							!isIOS() &&
							isSOVMobile
					})}>
					{(useAppState.brand !== BRANDS.SOV || !isSOVMobile) && (
						<legend className={styles.header}>
							<span className={styles.heading}>{_label}</span>
							{!isStackMobileLayout && <TextButton
								type="button"
								label={
									<FontAwesomeIcon
										icon={faTimes}
										className={styles.closeIcon}
									/>
								}
								className={styles.closeButton}
								onClick={handleClose}
							/>}
						</legend>
					)}
					{isSOVMobile && useAppState.brand === BRANDS.SOV && (
						<div className={styles.passengerClassField}>
							<SovPassengerClassFieldInput
								id={FLIGHTS_CLASS_ID}
								selectedClass={selectedClass}
								classList={classList}
								onChange={setSelectedClass}
							/>
						</div>
					)}

					<StepperInput
						id="adults"
						brand={useAppState.brand}
						className={styles.stepper}
						label={useAppState.getDictionary("adults")}
						labels={stepperLabels}
						description={useAppState.getDictionary("12-yrs")}
						customDescriptionClass={styles.description}
						value={selectedPassengers.adults}
						minValue={isSOVMobile ? VALIDATIONS.SOV_FLIGHTS.MIN_ADULTS : 1}
						maxValue={isSOVMobile ? VALIDATIONS.SOV_FLIGHTS.MAX_ADULTS : 6}
						onChange={handleAdultsChange}
					/>
					<StepperInput
						id="children"
						brand={useAppState.brand}
						className={styles.stepper}
						label={useAppState.getDictionary("children")}
						labels={stepperLabels}
						description={useAppState.getDictionary(
							maxChildAge > 0 ? `0-${maxChildAge}-yrs` : "0-17-yrs"
						)}
						customDescriptionClass={styles.description}
						value={selectedPassengers.children.length}
						minValue={isSOVMobile ? VALIDATIONS.SOV_FLIGHTS.MIN_CHILDREN : 0}
						maxValue={isSOVMobile ? VALIDATIONS.SOV_FLIGHTS.MAX_CHILDREN : 4}
						onChange={handleChildrenChange}
					/>
					{!!selectedPassengers.children.length && (
						<fieldset id="childrenAge" className={styles.childAgesContainer}>
							<legend>{useAppState.getDictionary("children-age")}</legend>
							<span
								className={styles.ageError}
								id="flights-passengers-age-error"
								aria-live={displayAgeError ? "polite" : "off"}>
								{!isValidAgeSelection.every(validAge => validAge) &&
									useAppState.getDictionary(
										"rooms-guests-validation-children-age"
									)}
							</span>
							<div className={styles.childAges}>
								{selectedPassengers.children.map((age, index) => (
									<SelectInput
										// eslint-disable-next-line react/no-array-index-key
										key={`age-${index}`}
										id={`age-${index}`}
										className={styles.ageSelector}
										label={`${useAppState.getDictionary("child")} ${index + 1}`}
										displayInline={true}
										value={selectedPassengers.children[index] ?? true}
										error={!(isValidAgeSelection[index] ?? true)}
										ariaDescribedby="flights-passengers-age-error"
										options={childAgeOptions}
										onChange={value => {
											const newChildren = [...selectedPassengers.children];
											newChildren[index] = value;
											resetValidation();
											onChange({
												...selectedPassengers,
												children: newChildren
											});
										}}
									/>
								))}
							</div>
							<span className={styles.description}>
								{useAppState.getDictionary("bp-flights-child-age-description")}
							</span>
						</fieldset>
					)}
					{(useAppState.brand !== BRANDS.SOV || !isSOVMobile) && (
						<div className={styles.footer}>
							<Button
								id={FLIGHTS_PASSENGERS_ID}
								type="button"
								brand={useAppState.brand}
								variation="secondary"
								label={useAppState.getDictionary("done")}
								onClick={handleClose}
							/>
						</div>
					)}
				</fieldset>
			)}
		</PopoverInput>
	);
};

PassengersField.propTypes = {
	mode: PropTypes.string,
	className: PropTypes.string,
	searchType: PropTypes.string,
	maxChildAge: PropTypes.number,

	openRoomAndGuest: PropTypes.bool,
	isGuestMobile: PropTypes.bool,
	isInitialSovMobileInputState: PropTypes.bool,

	classList: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string,
			items: PropTypes.arrayOf(
				PropTypes.shape({
					label: PropTypes.string,
					value: PropTypes.string
				})
			)
		})
	).isRequired,
	selectedPassengers: PropTypes.shape({
		rooms: PropTypes.number,
		adults: PropTypes.number,
		children: PropTypes.arrayOf(
			PropTypes.oneOfType([PropTypes.number, PropTypes.string])
		),
		infants: PropTypes.number
	}).isRequired,
	selectedClass: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),

	ParentRefElement: PropTypes.shape({ current: PropTypes.node }),

	setSelectedClass: PropTypes.func,
	setShowClassInput: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	setIsInitialSovMobileInputState: PropTypes.func,
	handleSovMobileInputSelectionToView: PropTypes.func
};

PassengersField.defaultProps = {
	mode: MODES.SEARCH,
	maxChildAge: 17,
	searchType: undefined,

	className: undefined,
	selectedClass: undefined,

	openRoomAndGuest: false,
	isGuestMobile: false,
	isInitialSovMobileInputState: false,

	ParentRefElement: null,

	setSelectedClass: () => {},
	setShowClassInput: () => {},
	setIsInitialSovMobileInputState: () => {},
	handleSovMobileInputSelectionToView: () => {}
};

export default PassengersField;
export { PassengersField };
